import React, { useState, useEffect, useRef, useContext } from "react";

import * as APIModels from "../../../models";
import MaterialTable, {
    Column,
    EditComponentProps,
} from "@material-table/core";
import { useCallback } from "react";
import pdfIcon from "../../../assets/images/file-icons/pdf.svg";
import zipIcon from "../../../assets/images/file-icons/zip.svg";
import docIcon from "../../../assets/images/file-icons/doc.svg";
import {
    FileService,
    SubmissionService,
} from "../../../services/SubmissionService";
import { formatBytes } from "../../../helpers/utility";
import useInterval from "../../../helpers/hooks/useInterval";
import { url } from "inspector";

export default function PreviewFileManager({
    uploadId,
    onGenerate,
    startGenerate,
    onComplete,
}: {
    uploadId: string;
    onGenerate: () => void;
    startGenerate: boolean;
    onComplete: () => void;
}) {
    const never = "never" as const;
    const [generating, setGenerating] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const iframeRef = useRef<HTMLEmbedElement>(null);
    const f: APIModels.FileInfo[] = [];
    const [files, setFiles] = useState(f);
    const [selectedFiles, setSelectedFiles] = useState(f);
    const [showTable, setShowTable] = useState(false);
    const [startGenerator, setStartGenerator] = useState(startGenerate);

    type Delay = number | null;
    const [delay, setDelay] = useState(5000);
    const [isRunning, setIsRunning] = useState(false);
    const [statusId, setStatusId] = useState("");
    const [request, setRequest] = useState(new APIModels.CaseSearch());

    const [hoverElement, setHoverElement] = useState(
        null as HTMLElement | null
    );

    useInterval(
        () => {
            FileService.previewList({
                uploadId: uploadId,
            }).then(
                (resp) => {
                    setErrorMessage(resp.errorMessage ?? "");
                    console.log(resp);
                    if (resp.data && resp.data?.length > 0) {
                        //We will need to show the modal to select which case
                        setFiles(resp.data ?? []);
                        setIsRunning(false);
                        setGenerating(false);
                        onComplete();
                    } else if (resp.errorMessage != null && !resp.successful) {
                        setFiles([]);
                        setIsRunning(false);
                        setGenerating(false);
                        onComplete();
                    }
                },
                (error) => {
                    setGenerating(false);
                    setIsRunning(false);
                    onComplete();
                    console.log(error);
                    setErrorMessage("An error has occurred");
                }
            );
        },
        isRunning ? delay : null
    );

    const downloadFile = (filename: string) => {
        let filesToSubmit = [...selectedFiles];
        if (filename && selectedFiles.length == 0) {
            filesToSubmit = [{ filename }];
        }

        filesToSubmit.forEach((element) => {
            FileService.previewLink({
                uploadId: uploadId,
                filename: element.filename!,
            }).then((resp) => {
                if (resp.data) {
                    const anchor = document.createElement("a");
                    anchor.href = resp.data;
                    anchor.target = "_blank";
                    anchor.download = element.filename!;
                    anchor.click();
                }
            });
        });
    };

    const onSelectFile = (index: number, file: APIModels.FileInfo) => {
        const matchingIndex = selectedFiles.findIndex(
            (v) => v.filename === file.filename
        );

        if (matchingIndex >= 0) {
            const filesUpdated = [...selectedFiles];
            filesUpdated.splice(matchingIndex, 1);
            setSelectedFiles(filesUpdated);
        } else {
            setSelectedFiles([...selectedFiles, file]);
        }

        return false;
    };

    const handleMouseOut = (event: React.MouseEvent<HTMLImageElement>) => {
        if (event.currentTarget == hoverElement) {
            setHoverElement(null);
        }
    };

    const handleMouseOver = (
        event: React.MouseEvent<HTMLImageElement>,
        file: APIModels.FileInfo
    ) => {
        if (file.filename!.indexOf(".pdf") > 0) {
            setHoverElement(event.currentTarget);

            FileService.previewLink({
                uploadId: uploadId,
                filename: file.filename!,
            }).then(
                (resp) => {
                    if (resp.data) {
                        if (iframeRef && iframeRef.current)
                            iframeRef.current.src = resp.data;
                    }
                },
                (error) => {
                    setErrorMessage(error.message);
                }
            );
        }
    };

    const hoverPreview = () => {
        const rect = hoverElement!.getBoundingClientRect();
        return (
            <div
                style={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    height: "800px",
                    width: "800px",
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            >
                <embed
                    ref={iframeRef}
                    type="application/pdf"
                    style={{ height: "800px", width: "800px" }}
                ></embed>
            </div>
        );
    };

    useEffect(() => {
        if (startGenerate) {
            setGenerating(true);
            setIsRunning(true);
        }
    }, [startGenerate]);

    return (
        <div className="card-body">
            <div className="mb-3">
                {generating ? (
                    <button
                        className="btn btn-primary waves-effect waves-light mt-1"
                        type="button"
                        disabled={true}
                    >
                        <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        Generating...
                    </button>
                ) : (
                    <button
                        className="btn btn-primary waves-effect waves-light mt-2"
                        type="submit"
                        onClick={() => {
                            onGenerate();
                            setFiles([]);
                            setGenerating(true);
                            setShowTable(true);
                        }}
                    >
                        Generate File Preview
                    </button>
                )}
                <label style={{ fontWeight: "bolc", padding: "10px" }}>
                    Please note: File preview generation may take some time to
                    complete.
                </label>
                <div style={{ color: "red" }}>{errorMessage}</div>
            </div>{" "}
            {hoverElement && hoverPreview()}
            {showTable ? (
                <table
                    className="table table-centered  table-nowrap mb-0"
                    style={{ zIndex: 0 }}
                >
                    <thead className="table-light">
                        <tr>
                            <th scope="col">File Name</th>
                            <th scope="col">Date Modified</th>
                            <th scope="col">Size</th>
                            <th
                                scope="col"
                                className="text-center"
                                style={{ width: 125 }}
                            >
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {files.map((file, i) => {
                            return (
                                <tr>
                                    {/*
                                      <td>
                                       
                                        <input
                                            type="checkbox"
                                            checked={
                                                selectedFiles.filter(
                                                    (v) =>
                                                        v.filename ===
                                                        file.filename
                                                ).length > 0
                                            }
                                            onChange={() =>
                                                onSelectFile(i, file)
                                            }
                                        />
                                    </td> */}

                                    <td>
                                        <img
                                            src={
                                                file.filename &&
                                                file.filename
                                                    .toLowerCase()
                                                    .endsWith(".pdf")
                                                    ? pdfIcon
                                                    : file
                                                          .filename!.toLowerCase()
                                                          .endsWith(".zip")
                                                    ? zipIcon
                                                    : docIcon
                                            }
                                            height="30"
                                            alt="icon"
                                            className="me-2"
                                            onMouseEnter={(e) =>
                                                handleMouseOver(e, file)
                                            }
                                            onMouseLeave={handleMouseOut}
                                        />
                                        <a
                                            href="javascript:void(0);"
                                            className="text-dark"
                                        >
                                            {file.filename}
                                        </a>
                                    </td>
                                    <td className="font-13">
                                        {file.lastUpdatedDateTime}
                                    </td>
                                    <td>{formatBytes(file.size ?? 0)}</td>
                                    {file.lastUpdatedDateTime ? (
                                        <td>
                                            {(selectedFiles.length == 0 ||
                                                selectedFiles.findIndex(
                                                    (v) =>
                                                        v.filename ===
                                                        file.filename
                                                ) == 0) && (
                                                <div
                                                    className="btn-group dropdown"
                                                    style={{
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    <a
                                                        href="#"
                                                        className="dropdown-toggle arrow-none btn btn-light btn-sm"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                        onClick={() => {
                                                            downloadFile(
                                                                file.filename!
                                                            );
                                                        }}
                                                    >
                                                        Download File
                                                    </a>
                                                </div>
                                            )}
                                        </td>
                                    ) : (
                                        <td>
                                            <div
                                                className="spinner-border text-primary m-2"
                                                role="status"
                                            >
                                                <span className="visually-hidden">
                                                    Loading...
                                                </span>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <></>
            )}
        </div>
    );
}
